// Taşeron portalı — Araç Sheet'inin belge listesi (§23.2). Araç kaydının belgeleri
// `ContractorVehicleDto.documents` / `WorkPermitVehicleDto.documents` içinde GÖMÜLÜ gelir; ayrı
// sorgu yok. Taşeron onay/red YAPMAZ ve uzman kimliğini görmez; yalnız indirir ve (yalnız
// `Pending` iken) siler. Karar verilmiş belgenin yolu "Belge yükle"dir.
//
// ⚠️ İki GUID: `doc.uuid` = araç belge kaydı → SİLME; `doc.documentUuid` = §8 dosyası → İNDİRME.
//
// Silmeden sonra odak: silinen satırın tetikleyicisi kalkar; odak durumla "Belge yükle"ye iner
// (zamanlayıcı DEĞİL — Modal'ın kapanış odak geri yüklemesi bittikten sonra çalışan efekt).

import {
  Button,
  ConfirmDialog,
  type ContractorVehicleDto,
  DocumentRow,
  EmptyState,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableMessageRow,
  TableRow,
  type VehicleDocumentDto,
  canDeleteDocument,
  currentCulture,
  documentNameDetail,
  documentValidityText,
  presentDocument,
  supplierContractorApi,
  useDocumentTypes,
} from '@isgportal/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FileText, Upload } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../lib/format';
import { invalidateVehicleCaches } from '../../lib/vehicleCache';
import { DocumentActions } from '../documents/DocumentActions';
import { VehicleDocumentUploadModal } from './VehicleDocumentUploadModal';

interface VehicleDocumentsProps {
  companyId: number | null;
  vehicle: Pick<ContractorVehicleDto, 'uuid' | 'plate' | 'documents'>;
}

export function VehicleDocuments({ companyId, vehicle }: VehicleDocumentsProps) {
  const { t } = useTranslation();
  // Tür etiketi §27 kaydından (GL #7); kayıtta yoksa/yüklenmemişse ham kod.
  const { label: typeLabelOf } = useDocumentTypes();
  const culture = currentCulture();
  const queryClient = useQueryClient();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [toDelete, setToDelete] = useState<VehicleDocumentDto | null>(null);
  const [deletedUuid, setDeletedUuid] = useState<string | null>(null);
  const uploadButtonRef = useRef<HTMLButtonElement>(null);

  const deleteMutation = useMutation({
    // SİLME: araç belge kaydının kendi `uuid`'i (§8 `documentUuid` DEĞİL — aksi 404).
    mutationFn: (documentUuid: string) =>
      supplierContractorApi.deleteSupplierVehicleDocument(vehicle.uuid, documentUuid),
    onSuccess: (_data, documentUuid) => {
      invalidateVehicleCaches(queryClient, companyId);
      setDeletedUuid(documentUuid);
      setToDelete(null);
    },
    // 409 VEHICLE_DOCUMENT_NOT_PENDING: bayat olgu — kutuda mesaj kalır, liste tazelenir.
    // Satır yenilemeyle kalkarsa odak yine "Belge yükle"ye insin (aşağıdaki efekt).
    onError: (_error, documentUuid) => {
      invalidateVehicleCaches(queryClient, companyId);
      setDeletedUuid(documentUuid);
    },
  });

  const documents = vehicle.documents;

  // Silinen satır listeden KALKINCA ve diyalog kapanınca odak <body>'ye düşmesin.
  useEffect(() => {
    if (
      deletedUuid !== null &&
      toDelete === null &&
      !documents.some((doc) => doc.uuid === deletedUuid)
    ) {
      uploadButtonRef.current?.focus();
      setDeletedUuid(null);
    }
  }, [documents, deletedUuid, toDelete]);

  const title = t('vehicles.sheet.docs.title');

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-fg">{title}</h2>
        <Button
          ref={uploadButtonRef}
          variant="secondary"
          size="sm"
          onClick={() => setUploadOpen(true)}
        >
          <Upload className="h-4 w-4" aria-hidden="true" />
          {t('vehicles.sheet.docs.upload')}
        </Button>
      </div>

      <Table caption={title}>
        <TableHeader>
          <TableRow className="carbon:h-0">
            <TableHead className="h-0 p-0 carbon:h-0">
              <span className="sr-only">{t('permits.vehicles.docs.col.type')}</span>
            </TableHead>
            <TableHead className="h-0 p-0 text-right carbon:h-0">
              <span className="sr-only">{t('common.actions')}</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {documents.length === 0 ? (
            <TableMessageRow colSpan={2}>
              <EmptyState icon={FileText} title={t('vehicles.sheet.docs.empty')} />
            </TableMessageRow>
          ) : (
            documents.map((doc) => {
              const typeLabel = typeLabelOf('vehicle', doc.type);
              const p = presentDocument(doc, t);
              const validity = documentValidityText(doc, (iso) => formatDate(iso, culture), t);
              return (
                <DocumentRow
                  key={doc.uuid}
                  title={typeLabel}
                  fileName={doc.fileName}
                  noteText={doc.note}
                  validityRangeText={validity}
                  quiet={p.quiet}
                  quietLabel={p.quietLabel}
                  tags={p.tags}
                  srExtra={p.srExtra}
                  statusContextLabel={t('permits.vehicles.docs.col.approval')}
                  rejectionReasonText={
                    doc.approvalStatus === 'Rejected' && doc.rejectionReason
                      ? t('permits.vehicles.docs.rejectionReason', { reason: doc.rejectionReason })
                      : null
                  }
                  actions={
                    <DocumentActions
                      doc={doc}
                      typeLabel={typeLabel}
                      nameDetail={documentNameDetail(doc, validity)}
                      canDelete={canDeleteDocument(doc, 'contractor')}
                      onDelete={() => setToDelete(doc)}
                    />
                  }
                />
              );
            })
          )}
        </TableBody>
      </Table>
      <p className="text-xs text-muted">{t('vehicles.sheet.docs.note')}</p>

      <VehicleDocumentUploadModal
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        companyId={companyId}
        vehicleUuid={vehicle.uuid}
        vehiclePlate={vehicle.plate}
      />
      <ConfirmDialog
        open={toDelete !== null}
        onOpenChange={(open) => {
          if (!open) {
            setToDelete(null);
            deleteMutation.reset();
            // Satır hâlâ listedeyse bayat odak hedefi bırakma.
            if (toDelete && documents.some((doc) => doc.uuid === toDelete.uuid)) {
              setDeletedUuid(null);
            }
          }
        }}
        title={t('permits.vehicles.docs.delete.title')}
        body={t('permits.vehicles.docs.delete.body', {
          type: toDelete ? typeLabelOf('vehicle', toDelete.type) : '',
        })}
        confirmLabel={t('permits.vehicles.docs.delete.confirm')}
        loading={deleteMutation.isPending}
        error={deleteMutation.isError ? deleteMutation.error : undefined}
        onConfirm={() => toDelete && deleteMutation.mutate(toDelete.uuid)}
      />
    </div>
  );
}
